<template>
  <div class="bulk-action-container">
    <form @submit.prevent="onSubmit()">
        <!-- Row 1: I Want To and Applies For -->
        <b-row>
          <b-col md="6">
            <div class="form-group">
              <label class="form-label">I Want To <span class="required">*</span></label>
              <div class="custom-select-wrapper" :class="{ 'has-validation': action_type }">
                <img src="@/assets/icons/bulk-action-icon.svg" alt="Bulk Action" class="select-icon" />
                <b-form-select 
                  name="type" 
                  v-model="action_type" 
                  :options="action_opt" 
                  :state="validate_action_type"
                  @change="onActionTypeChange"
                  class="custom-form-select"
                  :title="selectedActionTitle"
                  required>
                </b-form-select>
                <svg class="dropdown-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 10L12 15L17 10" stroke="#3A3A3A" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <b-form-invalid-feedback :state="validate_action_type">
                Select an action type
              </b-form-invalid-feedback>
            </div>
          </b-col>
          
          <b-col md="6" v-if="action_type">
            <div class="form-group">
              <label class="form-label">Applies For <span class="required">*</span></label>
              <div class="custom-select-wrapper" :class="{ 'has-validation': isAppliesToSelected }">
                <b-form-select 
                  name="appliesto" 
                  v-model="appliesto" 
                  :options="appliesto_opt" 
                  :state="isAppliesToSelected ? !invalid_appliesto_message : null"
                  @change="validate_appliesto"
                  class="custom-form-select"
                  required>
                </b-form-select>
                <svg class="dropdown-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 10L12 15L17 10" stroke="#3A3A3A" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <b-form-invalid-feedback v-if="isAppliesToSelected">
                {{ invalid_appliesto_message }}
              </b-form-invalid-feedback>
            </div>
          </b-col>
        </b-row>

        <!-- Select Monitors (when applicable) -->
        <div v-if="appliesto==2">
          <div class="form-group">
            <label class="form-label">Select Monitors <span class="required">*</span></label>
            <div class="custom-select-wrapper monitor-select" @click="toggleMonitorDropdown">
              <span class="select-placeholder">{{ monitorSelectText }}</span>
              <svg class="dropdown-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 10L12 15L17 10" stroke="#3A3A3A" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              
              <!-- Monitor Dropdown -->
              <div v-if="showMonitorDropdown" class="monitor-dropdown" @click.stop>
                <div class="monitor-search">
                  <input 
                    type="text" 
                    v-model="monitorSearchQuery" 
                    placeholder="Search monitors..."
                    class="monitor-search-input"
                    @click.stop
                  />
                </div>
                <div class="monitor-options">
                  <div 
                    v-for="monitor in filteredMonitorsList" 
                    :key="monitor.value"
                    class="monitor-option"
                    :class="{ 'selected': isMonitorSelected(monitor) }"
                    @click.stop="toggleMonitor(monitor)"
                  >
                    <input 
                      type="checkbox" 
                      :checked="isMonitorSelected(monitor)"
                      class="monitor-checkbox"
                    />
                    <span>{{ monitor.text }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="isMonitorInvalid" class="invalid-feedback d-block">
              {{ invalid_monitor_message }}
            </div>
          </div>
          
          <!-- Selected Monitors Display -->
          <div v-if="selected_monitors && selected_monitors.length > 0" class="selected-monitors-section">
            <div class="selected-monitors-tags">
              <span 
                v-for="monitor in selected_monitors" 
                :key="monitor.value"
                class="monitor-tag"
              >
                <span class="monitor-tag-dot"></span>
                {{ monitor.text }}
                <svg @click="removeMonitor(monitor)" class="tag-remove" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </div>
          </div>
        </div>

        <!-- Alert Frequency (when action_type == 8) -->
        <div v-if="action_type==8" class="form-group">
          <label class="form-label">Alert Frequency <span class="required">*</span></label>
          <div class="custom-select-wrapper">
            <b-form-select
              name="downAlertFreqType"
              v-model="input.downAlertFreqType"
              :options="alert_freq_opt"
              :state="isDownAlertFreqValid ? !invalid_down_alert_message : null" 
              @change="validateDownAlertFreq"
              class="custom-form-select"
              required>
              <b-form-select-option value=1 :disabled="not_premium" title="paid">{{not_premium?'once per hour (pro plan)':'once per hour'}}</b-form-select-option>
              <b-form-select-option value=2 :disabled="not_premium" title="paid">{{not_premium?'once per day (pro plan)':'once per day'}}</b-form-select-option>
              <b-form-select-option value=3 :disabled="not_premium" title="paid">{{not_premium?'once per week (pro plan)':'once per week'}}</b-form-select-option>
              <b-form-select-option value=4 :disabled="not_premium" title="paid">{{not_premium?'once per month (pro plan)':'once per month'}}</b-form-select-option>
            </b-form-select>
            <svg class="dropdown-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 10L12 15L17 10" stroke="#3A3A3A" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <b-form-invalid-feedback v-if="isDownAlertFreqValid">
            {{ invalid_down_alert_message }}
          </b-form-invalid-feedback>
        </div>

        <!-- Monitoring Interval (when action_type == 4) -->
        <div v-if="action_type == 4" class="form-group">
          <label class="form-label">Monitoring Interval</label>
          <div class="interval-container">
            <div class="interval-input-wrapper">
              <button type="button" class="interval-btn interval-btn-left" @click="decrementInterval" :disabled="intervalIndex <= 0">
                <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 1H12" stroke="currentColor" stroke-width="2"/>
                </svg>
              </button>
              <input 
                type="text" 
                :value="intervalDisplayValue" 
                readonly
                class="interval-input"
              />
              <button type="button" class="interval-btn interval-btn-right" @click="incrementInterval" :disabled="intervalIndex >= interval.data.length - 1">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 0V12M0 6H12" stroke="currentColor" stroke-width="2"/>
                </svg>
              </button>
            </div>
            <div class="interval-unit-wrapper">
              <b-form-select 
                v-model="intervalUnit" 
                class="custom-form-select interval-unit-select"
                disabled
              >
                <option value="minutes" v-if="interval.data[intervalIndex] <= 60">Minutes</option>
                <option value="hours" v-else>Hours</option>
              </b-form-select>
              <svg class="dropdown-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 10L12 15L17 10" stroke="#3A3A3A" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
        </div>

        <!-- Monitoring Timeout (when action_type == 5) -->
        <div v-if="action_type == 5" class="form-group">
          <label class="form-label">Monitoring Timeout</label>
          <div class="interval-container">
            <div class="interval-input-wrapper">
              <button 
                type="button" 
                class="interval-btn interval-btn-left" 
                @click="decrementMonitorTimeout"
                :disabled="input.timeout <= 1"
              >
                <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 1H12" stroke="currentColor" stroke-width="2"/>
                </svg>
              </button>
              <input 
                type="number" 
                v-model.number="input.timeout" 
                @input="validateMonitorTimeout"
                class="interval-input"
                min="1"
                max="60"
              />
              <button 
                type="button" 
                class="interval-btn interval-btn-right" 
                @click="incrementMonitorTimeout"
                :disabled="input.timeout >= 60"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 0V12M0 6H12" stroke="currentColor" stroke-width="2"/>
                </svg>
              </button>
            </div>
            <div class="interval-unit-wrapper">
              <b-form-select 
                v-model="timeoutUnit" 
                class="custom-form-select interval-unit-select"
                disabled
              >
                <option value="seconds">Seconds</option>
              </b-form-select>
              <svg class="dropdown-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 10L12 15L17 10" stroke="#3A3A3A" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
        </div>

        <!-- Grace Timeout (when action_type == 9) -->
        <div v-if="action_type == 9" class="form-group">
          <label class="form-label">Grace Timeout</label>
          <div class="interval-container">
            <div class="interval-input-wrapper">
              <button 
                type="button" 
                class="interval-btn interval-btn-left" 
                @click="decrementTimeout"
                :disabled="graceTimeoutIndex <= 0"
              >
                <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 1H12" stroke="currentColor" stroke-width="2"/>
                </svg>
              </button>
              <input 
                type="text" 
                :value="input.grace_interval" 
                readonly
                class="interval-input"
              />
              <button 
                type="button" 
                class="interval-btn interval-btn-right" 
                @click="incrementTimeout"
                :disabled="graceTimeoutIndex >= grace_timeout_datas.length - 1"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 0V12M0 6H12" stroke="currentColor" stroke-width="2"/>
                </svg>
              </button>
            </div>
            <div class="interval-unit-wrapper">
              <b-form-select 
                v-model="graceTimeoutUnit" 
                class="custom-form-select interval-unit-select"
                disabled
              >
                <option value="seconds">Seconds</option>
              </b-form-select>
              <svg class="dropdown-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 10L12 15L17 10" stroke="#3A3A3A" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
        </div>

        <!-- Alert Contacts (when action_type == 6) -->
        <div v-if="action_type == 6">
          <div class="form-group">
            <label class="form-label">Select Alert Contacts To Notify <span class="required">*</span></label>
            <div class="custom-select-wrapper alert-select" @click="toggleAlertDropdown">
              <span class="select-placeholder">{{ alertSelectText }}</span>
              <svg class="dropdown-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 10L12 15L17 10" stroke="#3A3A3A" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              
              <!-- Alert Dropdown -->
              <div v-if="showAlertDropdown" class="alert-dropdown" @click.stop>
                <div class="alert-search">
                  <input 
                    type="text" 
                    v-model="alertSearchQuery" 
                    placeholder="Search alert contacts..."
                    class="alert-search-input"
                    @click.stop
                  />
                </div>
                <div class="alert-options">
                  <div 
                    v-for="alert in filteredAlertsList" 
                    :key="alert.value"
                    class="alert-option"
                    :class="{ 'selected': isAlertSelected(alert) }"
                    @click.stop="toggleAlert(alert)"
                  >
                    <input 
                      type="checkbox" 
                      :checked="isAlertSelected(alert)"
                      class="alert-checkbox"
                    />
                    <span>{{ alert.text }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="isAlertInvalid" class="invalid-feedback d-block">
              {{ invalid_alert_message }}
            </div>
          </div>
          
          <!-- Selected Alert Contacts Display -->
          <div v-if="alertContacts && alertContacts.length > 0" class="selected-alerts-section">
            <div class="selected-alerts-tags">
              <span 
                v-for="alert in alertContacts" 
                :key="alert.value"
                class="alert-tag"
              >
                <span class="alert-tag-dot"></span>
                {{ alert.text }}
                <svg @click="removeAlertContact(alert)" class="tag-remove" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </div>
          </div>
        </div>

        <!-- Alert Contact Mode Selection (when action_type == 6) -->
        <div v-if="action_type == 6" class="form-group">
          <label class="form-label">Alert Contact Assignment Mode <span class="required">*</span></label>
          <div class="alert-mode-options">
            <label class="custom-radio">
              <input
                type="radio"
                name="alertContactMode"
                value="overwrite"
                v-model="alertContactMode"
                class="radio-input"
              />
              <span class="radio-checkmark"></span>
              <div class="radio-content">
                <span class="radio-label">Overwrite existing alerts</span>
                <span class="radio-description">Replace all current alert contacts with the selected ones</span>
              </div>
            </label>
            <label class="custom-radio">
              <input
                type="radio"
                name="alertContactMode"
                value="add"
                v-model="alertContactMode"
                class="radio-input"
              />
              <span class="radio-checkmark"></span>
              <div class="radio-content">
                <span class="radio-label">Add to existing alerts</span>
                <span class="radio-description">Keep current alert contacts and add the selected ones</span>
              </div>
            </label>
          </div>
        </div>

        <!-- SSL Settings (when action_type == 7) -->
        <div v-if="action_type == 7">
          <div class="form-group">
            <div class="ssl-checkbox-wrapper">
              <label class="custom-checkbox">
                <input type="checkbox" v-model="input.ssl_error" />
                <span class="checkbox-checkmark"></span>
                <span class="checkbox-label">Monitor SSL errors</span>
              </label>
            </div>
          </div>
          <div class="form-group">
            <div class="ssl-checkbox-wrapper">
              <label class="custom-checkbox">
                <input type="checkbox" v-model="input.ssl_expiry_reminder" />
                <span class="checkbox-checkmark"></span>
                <span class="checkbox-label">Enable SSL expiry reminders</span>
              </label>
              <p class="ssl-hint">Monitor URL needs to start with "https" for the SSL monitoring to work.</p>
            </div>
          </div>
        </div>

        <!-- Confirmation Section -->
        <div v-if="action_type" class="confirmation-container">
          <div class="confirmation-content">
            <h3 class="confirmation-label">Confirmation <span class="required">*</span></h3>
            <p class="confirmation-instruction">
              Please write <span class="action-highlight">"{{action_txt[action_type]}}"</span> to the field below to approve the action.
            </p>
            <div class="confirmation-input-box">
              <input 
                type="text" 
                v-model="approval_text" 
                @input="validateApprovalText"
                :placeholder="action_txt[action_type]"
                class="confirmation-input-field"
                :class="{ 'valid': isTextEntered && approval_text === action_txt[action_type], 'invalid': isTextEntered && approval_text !== action_txt[action_type] }"
              />
            </div>
          </div>
        </div>

        <!-- Loading Spinner -->
        <span v-if="isloading">
          <div class="loader-box">
            <div class="loader-30"></div>
          </div>
        </span>

        <!-- Submit Button -->
        <div class="form-actions">
          <button 
            type="submit" 
            class="action-btn" 
            :disabled="isloading || !canSubmit"
            :class="{ 'disabled': isloading || !canSubmit }"
          >
            Action Now
          </button>
        </div>
      </form>
  </div>
</template>

<script>
import MonitorsController from "../../services/monitorController";
import VueSlider from 'vue-slider-component';

export default {
  name: "BulkAction",
  components: { VueSlider },
  data() {
    return {
      isloading: false,
      alerts: [],
      alertContacts: [],
      plan_details: { minIntervalLimit: 5 },
      approval_text: '',
      input: {
        monitors: [],
        changePayload: {},
        interval: 5,
        timeout: 5,
        grace_interval: 30,
        ssl_expiry_reminder: false,
        ssl_error: false,
        downAlertFreqType: null
      },
      action_type: null,
      appliesto: null,
      action_opt: [
        { value: null, text: "Please Select" },
        { value: "1", text: "Pause Monitors" },
        { value: "2", text: "Start Monitors" },
        { value: "3", text: "Delete Monitors" },
        { value: "4", text: "Change Intervals Of Monitors" },
        { value: "5", text: "Change Timeouts Of Monitors" },
        { value: "6", text: "Change Alert Contacts Of Monitors" },
        { value: "7", text: "Change SSL Settings Of Monitors" },
        { value: "8", text: "Change Alert Frequency Of Monitors" },
        { value: "9", text: "Change Grace Timeout Of Monitors" },
      ],
      appliesto_opt: [
        { value: null, text: "Please Select" },
        { value: "1", text: "All monitors" },
        { value: "2", text: "Selected monitors" },
      ],
      action_txt: {
        1: 'PAUSE MONITORS',
        2: 'START MONITORS',
        3: 'DELETE MONITORS',
        4: 'CHANGE INTERVAL',
        5: 'CHANGE TIMEOUT',
        6: 'CHANGE ALERT CONTACTS',
        7: 'CHANGE SSL',
        8: 'CHANGE ALERT FREQUENCY',
        9: 'CHANGE GRACE TIMEOUT'
      },
      monitor_opt: [],
      all_monitors: [],
      selected_monitors: [],
      not_premium: false,
      alert_freq_opt: [
        { value: null, text: 'select frequency' },
        { value: 0, text: 'once' },
      ],
      interval: {
        data: [
          5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 120, 180, 240, 300, 360,
          420, 480, 540, 600, 660, 720, 780, 840, 900, 960, 1020, 1080, 1140,
          1200, 1260, 1320, 1380, 1440,
        ],
      },
      timeout: {
        data: Array.from({length: 60}, (_, i) => i + 1),
      },
      grace_timeout_datas: [30, 60, 120, 180, 300, 600, 1200, 1800, 3600, 7200, 10800, 18000, 36000, 54000, 72000, 86400],
      invalid_appliesto_message: 'Please select an applies to',
      isAppliesToSelected: false,
      invalid_monitor_message: 'Please select at least one monitor',
      isMonitorInvalid: false,
      isMonitorValid: false,
      invalid_alert_message: 'Please select at least one alert contact',
      isAlertInvalid: false,
      isAlertValid: false,
      isDownAlertFreqValid: false,
      invalid_down_alert_message: 'Please select an alert frequency',
      isTextEntered: false,
      invalid_approval_message: 'Approval text is required',
      intervalValue: 5,
      intervalUnit: 'minutes',
      showMonitorDropdown: false,
      monitorSearchQuery: '',
      showAlertDropdown: false,
      alertSearchQuery: '',
      timeoutUnit: 'seconds',
      graceTimeoutUnit: 'seconds',
      graceTimeoutIndex: 1, // default to 60 (index 1 in grace_timeout_datas)
      intervalIndex: 0, // default to 5 (index 0 in interval.data)
      alertContactMode: 'overwrite', // 'overwrite' or 'add'
    };
  },
  computed: {
    filteredMonitors() {
      if (this.action_type == 9) {
        return this.monitor_opt.filter(m => m.type == 5);
      }
      return this.monitor_opt;
    },
    validate_action_type() {
      return this.action_type !== null && this.action_type !== undefined;
    },
    canSubmit() {
      if (!this.action_type) return false;
      if (this.appliesto == 2 && (!this.selected_monitors || this.selected_monitors.length === 0)) return false;
      if (this.action_type == 6 && (!this.alertContacts || this.alertContacts.length === 0)) return false;
      if (this.approval_text !== this.action_txt[this.action_type]) return false;
      return true;
    },
    monitorSelectText() {
      if(!this.selected_monitors || this.selected_monitors.length === 0) {
        return 'Select a Monitor';
      }
      return `${this.selected_monitors.length} monitor${this.selected_monitors.length > 1 ? 's' : ''} selected`;
    },
    alertSelectText() {
      if(!this.alertContacts || this.alertContacts.length === 0) {
        return 'Select Alert Contacts';
      }
      return `${this.alertContacts.length} contact${this.alertContacts.length > 1 ? 's' : ''} selected`;
    },
    filteredMonitorsList() {
      const monitors = this.filteredMonitors;
      if(!this.monitorSearchQuery) {
        return monitors;
      }
      return monitors.filter(monitor => 
        monitor.text.toLowerCase().includes(this.monitorSearchQuery.toLowerCase())
      );
    },
    filteredAlertsList() {
      if(!this.alertSearchQuery) {
        return this.alerts;
      }
      return this.alerts.filter(alert => 
        alert.text.toLowerCase().includes(this.alertSearchQuery.toLowerCase())
      );
    },
    intervalDisplayValue() {
      const val = this.interval.data[this.intervalIndex];
      if (val <= 60) return val;
      return val / 60;
    },
    selectedActionTitle() {
      if (!this.action_type) return '';
      const selectedOption = this.action_opt.find(opt => opt.value === this.action_type);
      return selectedOption ? selectedOption.text : '';
    }
  },
  mounted() {
    this.plan_details = this.$root.$data.plan_details
    if (this.plan_details.plan_type == '1') {
      this.not_premium = true
    }
    this.getMonitors();
    this.getAlerts();
    this.updateIntervalDisplay();
    
    // Close dropdowns when clicking outside
    document.addEventListener('click', this.closeDropdownsOnClickOutside);

    // Set default interval to 5 if not already set
    const idx = this.interval.data.indexOf(this.input.interval);
    if (idx === -1) {
      this.input.interval = 5;
      this.intervalIndex = 0;
    } else {
      this.intervalIndex = idx;
    }

    // Set default grace timeout to 60 if not already set
    const idx2 = this.grace_timeout_datas.indexOf(this.input.grace_interval);
    if (idx2 === -1) {
      this.input.grace_interval = 60;
      this.graceTimeoutIndex = this.grace_timeout_datas.indexOf(60);
    } else {
      this.graceTimeoutIndex = idx2;
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeDropdownsOnClickOutside);
  },
  methods: {
    async onSubmit() {
      if (this.validate()) {
        this.isloading = true
        let monitors = []
        if (this.selected_monitors != null) {
          this.selected_monitors.map((t) => { monitors.push(t.value) })
        }
        var alertContacts = []
        if (this.alertContacts != null) {
          this.alertContacts.map((l) => {
            alertContacts.push(l.value)
          })
        }
        let payload = {}
        switch (this.action_type) {
          case '1': payload = { 'monitors': monitors, 'changePayload': { 'status': false } }
            break;
          case '2': payload = { 'monitors': monitors, 'changePayload': { 'status': true } }
            break;
          case '3': payload = { 'monitors': monitors, 'changePayload': { 'isDelete': true } }
            break;
          case '4': payload = { 'monitors': monitors, 'changePayload': { 'interval': this.input.interval } }
            break;
          case '5': payload = { 'monitors': monitors, 'changePayload': { 'timeout': this.input.timeout } }
            break;
          case '6': payload = { 'monitors': monitors, 'changePayload': { 'alertContacts': alertContacts }, 'alertContactMode': this.alertContactMode }
            break;
          case '7': payload = { 'monitors': monitors, 'changePayload': { 'ssl_expiry_reminder': this.input.ssl_expiry_reminder, ssl_error: this.input.ssl_error } }
            break;
          case '8': payload = { 'monitors': monitors, 'changePayload': { 'downAlertFreqType': this.input.downAlertFreqType } }
            break;
          case '9': payload = { 'monitors': monitors, 'changePayload': { 'grace_interval': this.input.grace_interval } }
            break;
        }
        let response = await MonitorsController.bulkAction(payload);
        if (response.result) {
          this.approval_text = ''
          this.$toasted.show(response.message, { theme: 'outline', position: "bottom-center", type: 'success', duration: 2000 });
          this.$emit('bulkmodal')
        }
        else {
          this.$toasted.show(response.message, { theme: 'outline', position: "bottom-center", type: 'error', duration: 3500 });
        }
        this.isloading = false
      }
    },
    validate() {
      if (this.appliesto == 2 && (!this.selected_monitors || this.selected_monitors.length === 0)) {
        this.$toasted.show('Please select monitor!', { theme: 'outline', position: "bottom-center", type: 'error', duration: 2000 });
        return false;
      }
      else if (this.action_type == 6 && this.alertContacts.length == 0) {
        this.$toasted.show('Please select alert contacts!', { theme: 'outline', position: "bottom-center", type: 'error', duration: 2000 });
        return false;
      }
      else if (this.approval_text === this.action_txt[this.action_type]) {
        return true;
      }
      else if (!this.appliesto) {
        this.$toasted.show('Please select applies to!', { theme: 'outline', position: "bottom-center", type: 'error', duration: 2000 });
        return false;
      }
      else {
        this.$toasted.show('Please enter correct approval text!', { theme: 'outline', position: "bottom-center", type: 'error', duration: 2000 });
        return false;
      }
    },
    async getAlerts() {
      let response = await MonitorsController.getAlerts()
      if (response.data) {
        response.data.map((t) => {
          if (t.status) {
            this.alerts.push({ value: t._id, text: t.name })
          }
        })
      }
    },
    async getMonitors() {
      let response = await MonitorsController.getMonitors();
      if (response.result) {
        response.data.map((t) => {
          this.monitor_opt.push({ value: t._id, text: t.name, type: t.type, isDown: t.isDown });
          this.all_monitors.push({ value: t._id, text: t.name, type: t.type, isDown: t.isDown });
        });
      }
    },
    validateSelectedMonitors() {
      if (!this.selected_monitors || this.selected_monitors.length === 0) {
        this.isMonitorInvalid = true;
        this.isMonitorValid = false;
        this.invalid_monitor_message = 'Please select at least one monitor';
      } else {
        this.isMonitorInvalid = false;
        this.isMonitorValid = true;
        this.invalid_monitor_message = '';
      }
    },
    validateAlertContacts() {
      if (!this.alertContacts || this.alertContacts.length === 0) {
        this.isAlertInvalid = true;
        this.isAlertValid = false;
        this.invalid_alert_message = 'Please select at least one alert contact';
      } else {
        this.isAlertInvalid = false;
        this.isAlertValid = true;
        this.invalid_alert_message = '';
      }
    },
    validate_appliesto() {
      this.isAppliesToSelected = true;
      if (!this.appliesto) {
        this.invalid_appliesto_message = 'Please select a applies to';
      } else {
        this.invalid_appliesto_message = '';
      }
    },
    validateDownAlertFreq() {
      this.isDownAlertFreqValid = true;
      if (!this.input.downAlertFreqType && this.input.downAlertFreqType != 0) {
        this.invalid_down_alert_message = 'Please select an alert frequency';
      } else {
        this.invalid_down_alert_message = '';
      }
    },
    validateApprovalText() {
      this.isTextEntered = true;
      if (!this.approval_text) {
        this.invalid_approval_message = 'Approval text is required';
      } else if (this.approval_text != this.action_txt[this.action_type]) {
        this.invalid_approval_message = 'Please enter correct approval text';
      } else {
        this.invalid_approval_message = '';
      }
    },
    onActionTypeChange() {
      // Reset dependent fields when action type changes
      this.appliesto = null;
      this.selected_monitors = [];
      this.alertContacts = [];
      this.approval_text = '';
      this.isAppliesToSelected = false;
      this.isTextEntered = false;
      this.monitorSearchQuery = '';
      this.alertSearchQuery = '';
      this.alertContactMode = 'overwrite';
    },
    updateIntervalDisplay() {
      if (this.input.interval <= 60) {
        this.intervalValue = this.input.interval;
        this.intervalUnit = 'minutes';
      } else {
        this.intervalValue = Math.round(this.input.interval / 60);
        this.intervalUnit = 'hours';
      }
    },
    updateInterval() {
      if (this.intervalUnit === 'minutes') {
        this.input.interval = parseInt(this.intervalValue) || 1;
      } else {
        this.input.interval = (parseInt(this.intervalValue) || 1) * 60;
      }
    },
    updateIntervalFromUnit() {
      this.updateInterval();
    },
    incrementInterval() {
      if (this.intervalIndex < this.interval.data.length - 1) {
        this.intervalIndex++;
        this.input.interval = this.interval.data[this.intervalIndex];
        this.updateIntervalUnit();
      }
    },
    decrementInterval() {
      if (this.intervalIndex > 0) {
        this.intervalIndex--;
        this.input.interval = this.interval.data[this.intervalIndex];
        this.updateIntervalUnit();
      }
    },
    updateIntervalUnit() {
      const val = this.interval.data[this.intervalIndex];
      this.intervalUnit = val <= 60 ? 'minutes' : 'hours';
    },
    removeMonitor(monitor) {
      const index = this.selected_monitors.findIndex(m => m.value === monitor.value);
      if (index > -1) {
        this.selected_monitors.splice(index, 1);
        this.validateSelectedMonitors();
      }
    },
    removeAlertContact(alert) {
      const index = this.alertContacts.findIndex(a => a.value === alert.value);
      if (index > -1) {
        this.alertContacts.splice(index, 1);
        this.validateAlertContacts();
      }
    },
    incrementMonitorTimeout() {
      if (this.input.timeout < 60) {
        this.input.timeout++;
      }
    },
    decrementMonitorTimeout() {
      if (this.input.timeout > 1) {
        this.input.timeout--;
      }
    },
    validateMonitorTimeout() {
      if (this.input.timeout < 1) {
        this.input.timeout = 1;
      } else if (this.input.timeout > 60) {
        this.input.timeout = 60;
      }
    },
    incrementTimeout() {
      if (this.graceTimeoutIndex < this.grace_timeout_datas.length - 1) {
        this.graceTimeoutIndex++;
        this.input.grace_interval = this.grace_timeout_datas[this.graceTimeoutIndex];
      }
    },
    decrementTimeout() {
      if (this.graceTimeoutIndex > 0) {
        this.graceTimeoutIndex--;
        this.input.grace_interval = this.grace_timeout_datas[this.graceTimeoutIndex];
      }
    },
    validateTimeout() {
      if (this.input.grace_interval < 1) {
        this.input.grace_interval = 1;
      } else if (this.input.grace_interval > 60) {
        this.input.grace_interval = 60;
      }
    },
    toggleMonitorDropdown() {
      this.showMonitorDropdown = !this.showMonitorDropdown;
      if(this.showMonitorDropdown) {
        this.monitorSearchQuery = '';
        this.showAlertDropdown = false;
      }
    },
    toggleAlertDropdown() {
      this.showAlertDropdown = !this.showAlertDropdown;
      if(this.showAlertDropdown) {
        this.alertSearchQuery = '';
        this.showMonitorDropdown = false;
      }
    },
    closeDropdownsOnClickOutside(event) {
      if (!event.target.closest('.monitor-select')) {
        this.showMonitorDropdown = false;
      }
      if (!event.target.closest('.alert-select')) {
        this.showAlertDropdown = false;
      }
    },
    isMonitorSelected(monitor) {
      return this.selected_monitors && this.selected_monitors.some(m => m.value === monitor.value);
    },
    isAlertSelected(alert) {
      return this.alertContacts.some(a => a.value === alert.value);
    },
    toggleMonitor(monitor) {
      if (!this.selected_monitors) {
        this.selected_monitors = [];
      }
      const index = this.selected_monitors.findIndex(m => m.value === monitor.value);
      if(index > -1) {
        this.selected_monitors.splice(index, 1);
      } else {
        this.selected_monitors.push(monitor);
      }
      this.validateSelectedMonitors();
    },
    toggleAlert(alert) {
      const index = this.alertContacts.findIndex(a => a.value === alert.value);
      if(index > -1) {
        this.alertContacts.splice(index, 1);
      } else {
        this.alertContacts.push(alert);
      }
      this.validateAlertContacts();
    }
  }
};
</script>

<style scoped>
/* Container */
.bulk-action-container {
  padding: 20px;
  width: 100%;
}

/* Form Group */
.form-group {
  margin-bottom: 32px !important;
}

.form-label {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #3A3A3A;
  margin-bottom: 10px;
  display: block;
}

.required {
  color: #E70000;
  margin-left: 2px;
}

/* Custom Select Wrapper */
.custom-select-wrapper {
  position: relative;
  background: transparent;
  border: 1px solid #D2E3FB;
  border-radius: 12px;
  padding: 0 12px;
  height: 48px;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: border-color 0.2s ease;
  overflow: visible;
}

.custom-select-wrapper:hover {
  border-color: #B3D1F8;
}

.select-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.custom-form-select {
  border: none !important;
  background: transparent !important;
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #3A3A3A;
  padding: 2px 50px 0 10px !important;
  height: auto;
  width: 100%;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  border-radius: 0 !important;
  box-shadow: none !important;
  cursor: pointer;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.custom-form-select:focus {
  box-shadow: none !important;
  outline: none !important;
  background: transparent !important;
  border: none !important;
}

.custom-form-select option {
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 16px;
}

.dropdown-arrow {
  position: absolute;
  right: 16px;
  pointer-events: none;
  transition: transform 0.2s ease;
}

/* Custom Select Styles */
.monitor-select,
.alert-select {
  cursor: pointer;
}

.select-placeholder {
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 16px;
  color: #3A3A3A;
  opacity: 0.6;
  flex: 1;
}

/* Monitor/Alert Dropdown */
.monitor-dropdown,
.alert-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: #FFFFFF;
  border: 1px solid #D2E3FB;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  z-index: 1000;
  max-height: 300px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.monitor-search,
.alert-search {
  padding: 12px;
  border-bottom: 1px solid #E5E7EB;
}

.monitor-search-input,
.alert-search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #D2E3FB;
  border-radius: 8px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  outline: none;
}

.monitor-search-input:focus,
.alert-search-input:focus {
  border-color: #3B82F6;
}

.monitor-options,
.alert-options {
  overflow-y: auto;
  max-height: 240px;
}

.monitor-option,
.alert-option {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  gap: 12px;
}

.monitor-option:hover,
.alert-option:hover {
  background-color: #F9FAFB;
}

.monitor-option.selected,
.alert-option.selected {
  background-color: #F3F4F6;
}

.monitor-checkbox,
.alert-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

/* Selected Monitors/Alerts Section */
.selected-monitors-section,
.selected-alerts-section {
  margin-top: 16px;
  margin-bottom: 32px;
}

.selected-monitors-tags,
.selected-alerts-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.monitor-tag,
.alert-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 12px;
  background: rgba(41, 51, 64, 0.15);
  border-radius: 20px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #3A3A3A;
  position: relative;
}

.monitor-tag-dot,
.alert-tag-dot {
  width: 6px;
  height: 6px;
  background-color: #6B7280;
  border-radius: 50%;
}

.tag-remove {
  cursor: pointer;
  transition: transform 0.2s ease;
}

.tag-remove:hover {
  transform: scale(1.1);
}

/* Interval Container */
.interval-container {
  display: flex;
  gap: 16px;
  align-items: stretch;
}

.interval-input-wrapper,
.interval-unit-wrapper {
  flex: 1 1 0;
  width: 50%;
  min-width: 0;
  box-sizing: border-box;
}

.interval-input-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #D2E3FB;
  border-radius: 12px;
  padding: 10px;
  height: 56px;
  gap: 30px;
}

.interval-unit-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid #D2E3FB;
  border-radius: 12px;
  padding-left: 20px;
  padding-right: 48px;
  height: 56px;
  position: relative;
}

.interval-btn {
  background: #FFFFFF;
  border: 1px solid #D2E3FB;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #3A3A3A;
  transition: all 0.2s ease;
  padding: 5px;
  flex-shrink: 0;
}

.interval-btn:hover:not(:disabled) {
  background: #F3F4F6;
}

.interval-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.interval-input {
  border: none;
  background: transparent;
  text-align: center;
  width: auto;
  min-width: 30px;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 0.915em;
  color: #3A3A3A;
  outline: none;
}

/* Remove spinner arrows */
.interval-input::-webkit-inner-spin-button,
.interval-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}

.interval-input[type=number] {
  -moz-appearance: textfield;
  appearance: textfield;
}

.interval-unit-select {
  width: 100%;
  height: 56px !important;
  border: none !important;
  border-radius: 12px !important;
  padding-left: 20px !important;
  padding-right: 48px !important;
  font-family: 'Poppins', sans-serif !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  line-height: 0.915em !important;
}

/* Timeout Container */
.timeout-container {
  display: flex;
  gap: 12px;
  align-items: stretch;
}

.timeout-input-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid #D2E3FB;
  border-radius: 12px;
  padding: 0;
  height: 48px;
  flex: 1;
  overflow: hidden;
}

.timeout-btn {
  background: transparent;
  border: none;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #3A3A3A;
  transition: all 0.2s ease;
  position: relative;
}

.timeout-btn-left {
  border-right: 1px solid #D2E3FB;
}

.timeout-btn-right {
  border-left: 1px solid #D2E3FB;
}

.timeout-btn:hover:not(:disabled) {
  background: #F3F4F6;
}

.timeout-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.timeout-input {
  border: none;
  background: transparent;
  text-align: center;
  flex: 1;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #3A3A3A;
  outline: none;
  padding: 0 16px;
}

/* Remove spinner arrows */
.timeout-input::-webkit-inner-spin-button,
.timeout-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}

.timeout-input[type=number] {
  -moz-appearance: textfield;
  appearance: textfield;
}

.timeout-unit-wrapper {
  display: flex;
  align-items: center;
  background: transparent;
  border: 1px solid #D2E3FB;
  border-radius: 12px;
  padding: 0 20px;
  height: 48px;
  flex: 1;
}

.timeout-unit-text {
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #3A3A3A;
  width: 100%;
}

/* SSL Checkbox */
.ssl-checkbox-wrapper {
  margin-bottom: 16px;
}

.custom-checkbox {
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  position: relative;
  padding-left: 32px;
  user-select: none;
}

.custom-checkbox input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkbox-checkmark {
  position: absolute;
  top: 2px;
  left: 0;
  height: 20px;
  width: 20px;
  background-color: #FFFFFF;
  border: 2px solid #D1D5DB;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.custom-checkbox:hover input ~ .checkbox-checkmark {
  border-color: #9CA3AF;
}

.custom-checkbox input:checked ~ .checkbox-checkmark {
  background-color: #2FB234;
  border-color: #2FB234;
}

.checkbox-checkmark:after {
  content: "";
  position: absolute;
  display: none;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.custom-checkbox input:checked ~ .checkbox-checkmark:after {
  display: block;
}

.checkbox-label {
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #3A3A3A;
  line-height: 24px;
}

.ssl-hint {
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #6B7280;
  margin: 8px 0 0 32px;
}

/* Confirmation Container */
.confirmation-container {
  background: #FFFFFF;
  border: 1px solid #D2E3FB;
  border-radius: 12px;
  padding: 20px;
  margin-top: 24px;
}

.confirmation-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.confirmation-label {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 0.915em;
  color: #3A3A3A;
  margin: 0;
}

.confirmation-instruction {
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.6em;
  color: #3A3A3A;
  margin: 0;
}

.action-highlight {
  color: #2563EB;
  font-weight: 600;
}

.confirmation-input-box {
  background: #FFFFFF;
  border: 1px solid #D2E3FB;
  border-radius: 12px;
  padding: 20px;
  height: 56px;
  display: flex;
  align-items: center;
}

.confirmation-input-field {
  width: 100%;
  border: none;
  outline: none;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 0.915em;
  color: #3A3A3A;
  background: transparent;
}

.confirmation-input-field::placeholder {
  color: #9CA3AF;
  opacity: 0.5;
}

.confirmation-input-field.valid {
  color: #010101;
}

.confirmation-input-field.invalid {
  color: #EF4444;
}

/* Action Button */
.form-actions {
  display: flex;
  justify-content: center;
  margin-top: 32px;
}

.action-btn {
  background: #2FB234;
  border: none;
  border-radius: 12px;
  padding: 12px 48px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #FFFFFF;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 200px;
  height: 56px;
  width: 100%;
  max-width: 100%;
}

.action-btn:hover:not(:disabled) {
  background: #28A02D;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(47, 178, 52, 0.25);
}

.action-btn.disabled,
.action-btn:disabled {
  background: #9CA3AF;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Invalid Feedback */
.invalid-feedback {
  display: none;
  color: #EF4444;
  font-size: 14px;
  margin-top: 6px;
}

.invalid-feedback.d-block {
  display: block !important;
}

/* Loading */
.loader-box {
  display: flex;
  justify-content: center;
  margin: 20px 0;
}

/* Responsive */
@media screen and (max-width: 768px) {
  .bulk-action-container {
    padding: 16px;
  }
  
  .interval-container,
  .timeout-container {
    flex-direction: column;
  }
  
  .interval-unit-wrapper,
  .timeout-unit-wrapper {
    width: 100%;
  }
  
  .action-btn {
    width: 100%;
  }
}

/* Override Bootstrap styles */
.form-control:focus {
  box-shadow: none !important;
}

/* Vue Slider customization */
.bulk-action-container >>> .vue-slider-rail {
  background-color: #E5E7EB !important;
}

.bulk-action-container >>> .vue-slider-process {
  background-color: #2FB234 !important;
}

.bulk-action-container >>> .vue-slider-dot-handle {
  background-color: #2FB234 !important;
  border: 2px solid #FFFFFF !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
}

/* Alert Mode Options */
.alert-mode-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
}

.custom-radio {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  padding: 16px;
  border: 1px solid #D2E3FB;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.custom-radio:hover {
  border-color: #B3D1F8;
  background-color: #F9FAFB;
}

.custom-radio input[type="radio"] {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.radio-checkmark {
  position: relative;
  top: 2px;
  height: 20px;
  width: 20px;
  background-color: #FFFFFF;
  border: 2px solid #D1D5DB;
  border-radius: 50%;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.custom-radio input[type="radio"]:checked ~ .radio-checkmark {
  background-color: #2FB234;
  border-color: #2FB234;
}

.radio-checkmark:after {
  content: "";
  position: absolute;
  display: none;
  left: 6px;
  top: 2px;
  width: 6px;
  height: 12px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.custom-radio input[type="radio"]:checked ~ .radio-checkmark:after {
  display: block;
}

.radio-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.radio-label {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: #3A3A3A;
  line-height: 1.4;
}

.radio-description {
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 13px;
  color: #6B7280;
  line-height: 1.5;
}
</style>